import React from 'react';
import { createRoot } from 'react-dom/client';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import './Toast.scss';

export type ToastVariant = 'info' | 'success' | 'warning';

interface ToastItem {
    id: number;
    text: string;
    variant: ToastVariant;
}

const AUTO_DISMISS_MS = 4000;

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<(items: ToastItem[]) => void>();

function emit(): void {
    listeners.forEach(listener => listener(toasts));
}

function dismiss(id: number): void {
    toasts = toasts.filter(item => item.id !== id);
    emit();
}

function subscribe(listener: (items: ToastItem[]) => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): ToastItem[] {
    return toasts;
}

function ToastViewport(): React.ReactElement {
    const items = React.useSyncExternalStore(subscribe, getSnapshot);
    const reduced = useReducedMotion();

    return (
        <div className="rd-toast-layer" aria-live="polite">
            <AnimatePresence>
                {items.map(item => (
                    <motion.div
                        key={item.id}
                        className={`rd-toast rd-toast--${item.variant}`}
                        initial={
                            reduced
                                ? { opacity: 0 }
                                : { opacity: 0, y: -12, scale: 0.98 }
                        }
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={
                            reduced
                                ? { opacity: 0 }
                                : { opacity: 0, y: -8, scale: 0.98 }
                        }
                        transition={{ duration: reduced ? 0 : 0.18 }}
                    >
                        <span>{item.text}</span>
                        <button
                            type="button"
                            className="rd-toast__close"
                            aria-label="Закрыть уведомление"
                            onClick={() => dismiss(item.id)}
                        >
                            ×
                        </button>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}

let mounted = false;

function mountViewport(): void {
    if (mounted || typeof document === 'undefined') return;
    mounted = true;
    const el = document.createElement('div');
    el.className = 'rd-toast-root';
    document.body.appendChild(el);
    createRoot(el).render(<ToastViewport />);
}
export function showToast(text: string, variant: ToastVariant = 'info'): void {
    mountViewport();
    const id = nextId;
    nextId += 1;
    toasts = [...toasts, { id, text, variant }];
    emit();
    window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
}

export default showToast;
